import * as React from 'react'
import { Icon } from './icon'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'

export interface InfoTooltipProps {
	/**
	 * Trigger element (e.g., button, tag, icon)
	 */
	children: React.ReactNode
	/**
	 * Tooltip title text
	 */
	title: string
	/**
	 * Tooltip content (can be text or JSX)
	 */
	content: React.ReactNode
	/**
	 * Whether to show the close button
	 */
	showCloseButton?: boolean
	/**
	 * Tooltip width (default: 320px)
	 */
	width?: number | string
	/**
	 * Tooltip side position relative to trigger
	 */
	side?: 'top' | 'right' | 'bottom' | 'left'
	/**
	 * Tooltip alignment relative to trigger
	 */
	align?: 'start' | 'center' | 'end'
	/**
	 * Distance from trigger in pixels
	 */
	sideOffset?: number
	/**
	 * Whether to show the arrow pointer
	 */
	showArrow?: boolean
}

export const InfoTooltip = React.forwardRef<HTMLDivElement, InfoTooltipProps>(
	(
		{
			children,
			title,
			content,
			showCloseButton = true,
			width = 320,
			side = 'bottom',
			align = 'end',
			sideOffset = 8,
			showArrow = true,
		},
		ref,
	) => {
		const widthStyle = typeof width === 'number' ? `${width}px` : width

		return (
			<Tooltip>
				<TooltipTrigger asChild>{children}</TooltipTrigger>
				<TooltipContent
					ref={ref}
					side={side}
					align={align}
					sideOffset={sideOffset}
					className="border-0 bg-white p-4 shadow-[0px_5px_10px_0px_rgba(217,217,217,1)]"
					style={{ width: widthStyle, borderRadius: '12px' }}
				>
					{/* Arrow - only shown if showArrow is true */}
					{showArrow && (
						<div
							className="absolute"
							style={{
								top: side === 'bottom' ? '-8px' : undefined,
								bottom: side === 'top' ? '-8px' : undefined,
								left: side === 'right' ? '-8px' : undefined,
								right:
									side === 'left'
										? '-8px'
										: align === 'end'
											? '16px'
											: align === 'start'
												? '16px'
												: '50%',
								transform:
									align === 'center' && (side === 'top' || side === 'bottom')
										? 'translateX(-50%)'
										: undefined,
							}}
						>
							<div
								className="h-2 w-7"
								style={{
									transform:
										side === 'bottom'
											? 'rotate(180deg)'
											: side === 'top'
												? 'rotate(0deg)'
												: side === 'right'
													? 'rotate(90deg)'
													: 'rotate(-90deg)',
								}}
							>
								<svg
									viewBox="0 0 28 8"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									className="h-full w-full"
								>
									<path
										d="M14 0L28 8H0L14 0Z"
										fill="white"
										filter="drop-shadow(0px -2px 4px rgba(217, 217, 217, 0.3))"
									/>
								</svg>
							</div>
						</div>
					)}

					<div className="flex flex-col gap-2">
						{/* Title with optional close button */}
						<div className="flex w-full items-center justify-between border-b border-neutral-300 pb-2">
							<p className="font-parkinsans text-sm leading-5 font-medium text-blue-800">
								{title}
							</p>
							{showCloseButton && (
								<button
									className="flex size-4 items-center justify-center text-neutral-600 transition-colors hover:text-neutral-800"
									aria-label="Cerrar"
								>
									<Icon name="cross-1" className="size-4" />
								</button>
							)}
						</div>

						{/* Content */}
						<div className="font-inter text-xs leading-[19px] font-normal text-neutral-700">
							{content}
						</div>
					</div>
				</TooltipContent>
			</Tooltip>
		)
	},
)

InfoTooltip.displayName = 'InfoTooltip'
